/**
 * Feed Composer effects — side-effect runner.
 *
 * Elm-ish architecture: effects are the ONLY place with side effects.
 * - API calls
 * - Navigation
 * - Browser APIs
 *
 * Effects are queued by the reducer and executed outside it.
 *
 * CORRECTION01 changes:
 * - R4: Dependencies injected via ComposerDependencies interface
 * - R7: AbortSignal support for cancellation and unmount cleanup
 * - R9: Safe error projection — never expose arbitrary Error.message
 * - R6: RegistrationKind drives exact-one profile authority selection
 */
import type { ComposerEffect } from './update';
import type { ComposerMsg } from './messages';
import type { AuthorSummary } from './model';
import type { CreatePostResult } from '@/api/feedPosts';

// =============================================================================
// Safe user-facing error messages
// =============================================================================

/**
 * Safe user-facing messages — no internal details leaked.
 * CORRECTION01: These are the ONLY user-visible error strings (R9).
 *
 * ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01:
 *   `session_required` is rendered with a recoverable session/security
 *   message ("Не удалось подтвердить сессию. Обновите страницу и
 *   попробуйте снова.") that is intentionally distinct from the
 *   legacy `forbidden` copy ("У вас нет прав для публикации.").
 *
 *   The legacy `forbidden` copy is preserved verbatim for callers
 *   that still match on it (it is the business authorization gate
 *   message; CSRF failures MUST NOT reuse it).
 */
const SAFE_ERROR_MESSAGES = {
  unavailable: 'Сервис публикации временно недоступен. Попробуйте позже.',
  unexpected: 'Не удалось опубликовать запись. Попробуйте ещё раз.',
  unauthenticated: 'Сессия истекла. Войдите снова.',
  // CSRF/security/session failure (RequireCSRF rejected the request,
  // or the helper could not bootstrap a token). Distinct copy so the
  // user is not falsely told they lack publication rights.
  session_required:
    'Не удалось подтвердить сессию. Обновите страницу и попробуйте снова.',
  // Business authorization gate (e.g. inactive profile). Legacy
  // copy preserved for backward-compatible callers.
  forbidden: 'У вас нет прав для публикации.',
  conflict: 'Публикация не удалась. Попробуйте ещё раз.',
  validation_failed: 'Не удалось опубликовать запись. Попробуйте ещё раз.',
} as const;

/**
 * Map a CreatePostResult to a safe user-facing message.
 * CORRECTION01: Post content is NEVER included (R9).
 *
 * ACT-INDEEP-PRODUCTION-FEED-CSRF-SESSION-CONTRACT01:
 *   `session_required` and `forbidden` MUST map to distinct strings;
 *   the regression tests in __tests__/effects.test.ts pin this.
 */
function mapResultToError(result: CreatePostResult): string {
  switch (result.kind) {
    case 'unavailable':
      return SAFE_ERROR_MESSAGES.unavailable;
    case 'unauthenticated':
      return SAFE_ERROR_MESSAGES.unauthenticated;
    case 'session_required':
      return SAFE_ERROR_MESSAGES.session_required;
    case 'forbidden':
      return SAFE_ERROR_MESSAGES.forbidden;
    case 'conflict':
      return SAFE_ERROR_MESSAGES.conflict;
    case 'validation_failed':
      return SAFE_ERROR_MESSAGES.validation_failed;
    case 'unexpected_failure':
      return SAFE_ERROR_MESSAGES.unexpected;
    case 'created':
      return SAFE_ERROR_MESSAGES.unexpected;
    default:
      return SAFE_ERROR_MESSAGES.unexpected;
  }
}

/**
 * Map an unknown error to a safe generic message.
 * CORRECTION01: Never expose err.message, stack, or response body (R9).
 */
function mapUnknownErrorToSafe(_err: unknown): string {
  return SAFE_ERROR_MESSAGES.unexpected;
}

// =============================================================================
// Author profile loader
// =============================================================================

// CORRECTION02: Author loading is fully injected — no concrete profile API imports (R6)
// Production adapter lives in FeedComposePage composition root.

// =============================================================================
// Dependencies interface
// =============================================================================

/**
 * Dependency interface for feed composer effects.
 * CORRECTION01: Enables dependency injection (R4).
 *
 * The page or application composition root owns the concrete adapters.
 */
export interface ComposerDependencies {
  /**
   * Post creation port.
   * Production adapter returns 'unavailable' until backend is ready.
   */
  readonly createPost: {
    create(
      command: { readonly content: string; readonly anonymous: boolean },
      signal: AbortSignal
    ): Promise<CreatePostResult>;
  };
  /**
   * Author loading port — fully injected (R6).
   * Production adapter calls exactly one profile authority based on registrationKind.
   * No AbortSignal support on underlying APIs: caller checks signal before and after.
   */
  readonly loadAuthor: {
    load(signal: AbortSignal): Promise<AuthorSummary | null>;
  };
}

// =============================================================================
// Effect context
// =============================================================================

/**
 * Context passed to runEffect so effects can dispatch messages back.
 */
export interface ComposerEffectContext {
  dispatch: (msg: ComposerMsg) => void;
}

// =============================================================================
// Effect runners
// =============================================================================

/**
 * Run a single effect with cancellation support.
 *
 * CORRECTION01: All effects receive AbortSignal for:
 * - Component unmount cleanup (R7)
 * - Replacement by newer submission (R7)
 * - Route departure (R7)
 *
 * @param effect — The effect to run
 * @param ctx — Effect context for dispatching messages
 * @param deps — Injected dependencies (R4)
 * @param signal — AbortSignal for cancellation (R7)
 */
export async function runEffect(
  effect: ComposerEffect,
  ctx: ComposerEffectContext,
  deps: ComposerDependencies,
  signal: AbortSignal
): Promise<void> {
  switch (effect.type) {
    case 'LoadAuthor': {
      // CORRECTION02j-rev2 — observability on both sides of the two
      // signal.aborted guards. Pure console-log. No behavior change.
      // Rationale: with rev1 the "result" marker was AFTER the abort
      // guard, so a promise that resolved/rejected during the await
      // window but then hit an already-aborted signal never produced
      // marker #8 at all — collapsing "promise hung" (L2) into
      // "promise resolved/rejected but aborted" (L3/A3). rev2 fires
      // the settled marker BEFORE the abort guard so resolution kind
      // is always observable, then logs the abort stage at the stop
      // point. Classification table in
      // docs/epics/feed-composer-api-wiring01/wal.md CORRECTION02j-rev2.
      // eslint-disable-next-line no-console
      console.log(
        `[composer-load-author-enter] aborted=${String(signal.aborted)}`
      );
      // Check if already aborted before starting
      if (signal.aborted) {
        // eslint-disable-next-line no-console
        console.log('[composer-load-author-stop] stage=entry-aborted');
        return;
      }
      try {
        const author = await deps.loadAuthor.load(signal);
        // IMPORTANT: log settlement kind + signal state BEFORE the
        // post-resolve abort guard so cancellation vs hang is
        // observable.
        // eslint-disable-next-line no-console
        console.log(
          `[composer-load-author-settled] kind=resolved aborted=${String(
            signal.aborted
          )}`
        );
        if (signal.aborted) {
          // eslint-disable-next-line no-console
          console.log(
            '[composer-load-author-stop] stage=post-resolve-aborted'
          );
          return;
        }
        // eslint-disable-next-line no-console
        console.log('[composer-authorloaded-dispatch]');
        ctx.dispatch({ type: 'AuthorLoaded', author });
      } catch (_err) {
        // eslint-disable-next-line no-console
        console.log(
          `[composer-load-author-settled] kind=rejected aborted=${String(
            signal.aborted
          )}`
        );
        if (signal.aborted) {
          // eslint-disable-next-line no-console
          console.log(
            '[composer-load-author-stop] stage=post-reject-aborted'
          );
          return;
        }
        ctx.dispatch({ type: 'AuthorLoadFailed' });
      }
      break;
    }
    case 'SubmitPost': {
      // Check if already aborted before starting
      if (signal.aborted) return;
      const { submissionId, content, anonymous } = effect;
      try {
        const result = await deps.createPost.create(
          { content, anonymous },
          signal
        );
        // Ignore if aborted during the call
        if (signal.aborted) return;
        if (result.kind === 'created') {
          ctx.dispatch({ type: 'PublishSucceeded', submissionId, postId: result.postId });
        } else {
          ctx.dispatch({ type: 'PublishFailed', submissionId, error: mapResultToError(result) });
        }
      } catch (err) {
        // Ignore if aborted during the call (R7)
        if (signal.aborted) return;
        ctx.dispatch({
          type: 'PublishFailed',
          submissionId,
          error: mapUnknownErrorToSafe(err),
        });
      }
      break;
    }
  }
}
